import { reportAdapters, type ReportAdapter, type ReportAdapterKey } from "./adapters";

export type ReportCategory = string;

export type ReportDefinition<K extends ReportAdapterKey = ReportAdapterKey> = {
  id: string;
  reportKey: K;
  name: string;
  description: string;
  category: ReportCategory;
  adapter: ReportAdapter<K>;
};

export type AnyReportDefinition = {
  [K in ReportAdapterKey]: ReportDefinition<K>;
}[ReportAdapterKey];

export const report_definitions: AnyReportDefinition[] = [
  {
    id: "budget-vs-actual",
    reportKey: "budget-vs-actual",
    name: "Budget vs Actual",
    description: "Budget, actual posted activity, and variance by fund/department/account",
    category: "finance",
    adapter: reportAdapters["budget-vs-actual"],
  },
  {
    id: "batch-contribution-summary",
    reportKey: "batch-contribution-summary",
    name: "Batch Contribution Summary",
    description: "Summary breakdown for a specific contribution batch",
    category: "finance",
    adapter: reportAdapters["batch-contribution-summary"],
  },
  {
    id: "giving-gap-report",
    reportKey: "giving-gap-report",
    name: "Giving Gap Report",
    description: "Lapsed givers who gave in the prior period but not the current period",
    category: "finance",
    adapter: reportAdapters["giving-gap-report"],
  },
  {
    id: "monthly-giving-summary",
    reportKey: "monthly-giving-summary",
    name: "Monthly Giving Summary",
    description: "Total contributions by fund for the current month",
    category: "finance",
    adapter: reportAdapters["monthly-giving-summary"],
  },
  {
    id: "year-end-giving-statements",
    reportKey: "year-end-giving-statements",
    name: "Year-End Giving Statements",
    description: "Household giving statements for tax purposes, formatted for #10 envelopes",
    category: "finance",
    adapter: reportAdapters["year-end-giving-statements"],
  },
  {
    id: "weekly-attendance-report",
    reportKey: "weekly-attendance-report",
    name: "Weekly Attendance Report",
    description: "Service attendance counts by event type",
    category: "attendance",
    adapter: reportAdapters["weekly-attendance-report"],
  },
  {
    id: "whos-missing",
    reportKey: "whos-missing",
    name: "Who’s Missing?",
    description: "Regular attenders who have been absent recently",
    category: "attendance",
    adapter: reportAdapters["whos-missing"],
  },
  {
    id: "new-people-this-month",
    reportKey: "new-people-this-month",
    name: "New People This Month",
    description: "People added to the directory this month",
    category: "people",
    adapter: reportAdapters["new-people-this-month"],
  },
  {
    id: "member-directory",
    reportKey: "member-directory",
    name: "Member Directory",
    description: "Printable directory with photos and contact details",
    category: "people",
    adapter: reportAdapters["member-directory"],
  },
  {
    id: "open-requests-by-age",
    reportKey: "open-requests-by-age",
    name: "Open Requests by Age",
    description: "All open requests sorted by days open",
    category: "ops",
    adapter: reportAdapters["open-requests-by-age"],
  },
  {
    id: "project-status-overview",
    reportKey: "project-status-overview",
    name: "Project Status Overview",
    description: "Projects with completion and target dates",
    category: "ops",
    adapter: reportAdapters["project-status-overview"],
  },
  // New finance reports
  { id: "statement-of-activity", reportKey: "statement-of-activity", name: "Statement of Activity", description: "Revenue and expenses for a date range with fund breakdown", category: "finance", adapter: reportAdapters["statement-of-activity"] },
  { id: "balance-sheet", reportKey: "balance-sheet", name: "Balance Sheet", description: "Assets, liabilities, and net assets as of a selected date", category: "finance", adapter: reportAdapters["balance-sheet"] },
  { id: "trial-balance", reportKey: "trial-balance", name: "Trial Balance", description: "All GL accounts with debit and credit balances for a period", category: "finance", adapter: reportAdapters["trial-balance"] },
  { id: "general-ledger-detail", reportKey: "general-ledger-detail", name: "General Ledger Detail", description: "All transactions posted to each GL account for a date range", category: "finance", adapter: reportAdapters["general-ledger-detail"] },
  { id: "cash-flow-statement", reportKey: "cash-flow-statement", name: "Cash Flow Statement", description: "Operating, investing, and financing cash flow activities", category: "finance", adapter: reportAdapters["cash-flow-statement"] },
  { id: "ar-aging", reportKey: "ar-aging", name: "Accounts Receivable Aging", description: "AR aging buckets by customer (current, 30, 60, 90, 120+ days)", category: "finance", adapter: reportAdapters["ar-aging"] },
  { id: "ap-aging", reportKey: "ap-aging", name: "Accounts Payable Aging", description: "AP aging buckets by vendor (current, 30, 60, 90, 120+ days)", category: "finance", adapter: reportAdapters["ap-aging"] },
  { id: "fund-balance-summary", reportKey: "fund-balance-summary", name: "Fund Balance Summary", description: "All funds with opening balance, activity, and closing balance", category: "finance", adapter: reportAdapters["fund-balance-summary"] },
  { id: "bank-reconciliation-report", reportKey: "bank-reconciliation-report", name: "Bank Reconciliation Report", description: "Reconciled vs unreconciled transactions per bank account", category: "finance", adapter: reportAdapters["bank-reconciliation-report"] },
  { id: "contribution-detail", reportKey: "contribution-detail", name: "Contribution Detail", description: "All contributions for a date range with donor and fund details", category: "finance", adapter: reportAdapters["contribution-detail"] },
  { id: "functional-expenses", reportKey: "functional-expenses", name: "Statement of Functional Expenses", description: "Expense matrix by natural classification (rows) and functional classification (columns) per ASC 958-720-45", category: "finance", adapter: reportAdapters["functional-expenses"] },
  { id: "liquidity-disclosure", reportKey: "liquidity-disclosure", name: "Liquidity & Availability Disclosure", description: "Financial assets available to meet general expenditures within one year per ASU 2016-14", category: "finance", adapter: reportAdapters["liquidity-disclosure"] },
  // New cross-module reports
  { id: "volunteer-schedule-report", reportKey: "volunteer-schedule-report", name: "Volunteer Schedule", description: "Volunteer assignments by ministry, team, and service date", category: "ops", adapter: reportAdapters["volunteer-schedule-report"] },
  { id: "group-roster-report", reportKey: "group-roster-report", name: "Group Roster", description: "Members of a group with contact info and roles", category: "people", adapter: reportAdapters["group-roster-report"] },
  { id: "giving-by-fund", reportKey: "giving-by-fund", name: "Giving by Fund", description: "Total giving per fund with prior period and budget comparison", category: "finance", adapter: reportAdapters["giving-by-fund"] },
  // Intelligence & operational reports (Epics 1–3)
  {
    id: "financial-variance",
    reportKey: "financial-variance",
    name: "Financial Variance",
    description: "Month-over-month giving variance with seasonal adjustment and anomaly z-scores",
    category: "finance",
    adapter: reportAdapters["financial-variance"],
  },
  {
    id: "fund-health",
    reportKey: "fund-health",
    name: "Fund Health",
    description: "Fund balances, runway projections, and over-restriction risk flags",
    category: "finance",
    adapter: reportAdapters["fund-health"],
  },
  {
    id: "engagement-health",
    reportKey: "engagement-health",
    name: "Engagement Health",
    description: "Composite engagement scoring across attendance, giving, volunteering, and care",
    category: "people",
    adapter: reportAdapters["engagement-health"],
  },
  {
    id: "care-load",
    reportKey: "care-load",
    name: "Care Load",
    description: "Care team workload distribution with burnout risk flags and aging request tracking",
    category: "ops",
    adapter: reportAdapters["care-load"],
  },
  { id: "endowment-performance", reportKey: "endowment-performance", name: "Endowment Performance", description: "Endowment fund performance, spending policy compliance, and distribution tracking", category: "finance", adapter: reportAdapters["endowment-performance"] },
  { id: "inkind-contributions", reportKey: "inkind-contributions", name: "In-Kind Contributions", description: "Non-cash gifts with fair market value, GAAP recognition status, and Form 8283 flagging", category: "finance", adapter: reportAdapters["inkind-contributions"] },
  { id: "pledge-receivable-aging", reportKey: "pledge-receivable-aging", name: "Pledge Receivable Aging", description: "Multi-year pledge receivables with present value discounting, amortization, and aging buckets", category: "finance", adapter: reportAdapters["pledge-receivable-aging"] },
  // Ministry reports
  { id: "ministry-growth", reportKey: "ministry-growth", name: "Ministry Growth", description: "Membership changes across ministries — new joiners, departures, and net growth rate", category: "ministry", adapter: reportAdapters["ministry-growth"] },
  { id: "ministry-participation", reportKey: "ministry-participation", name: "Ministry Participation", description: "Participation rates and event attendance across all active ministries", category: "ministry", adapter: reportAdapters["ministry-participation"] },
  { id: "ministry-volunteer-utilization", reportKey: "ministry-volunteer-utilization", name: "Ministry Volunteer Utilization", description: "Volunteer slot fill rates and utilization by ministry and team", category: "ministry", adapter: reportAdapters["ministry-volunteer-utilization"] },
  { id: "sacramental-records", reportKey: "sacramental-records", name: "Sacramental Records", description: "Baptisms, confirmations, weddings, funerals, and other sacramental records", category: "ministry", adapter: reportAdapters["sacramental-records"] },
];

export const reportDefinitionByKey = report_definitions.reduce((acc, definition) => {
  acc[definition.reportKey] = definition;
  return acc;
}, {} as Record<ReportAdapterKey, AnyReportDefinition>);

export const getReportDefinition = (reportKey: ReportAdapterKey): AnyReportDefinition =>
  reportDefinitionByKey[reportKey];
